import * as yup from "yup";

export interface ICrmMapping {
  id?: number;
  company_Id: number | null;
  branch_Id: number | null;
  department_Id: number | null;
  company_Ids?: number[];
  branch_Ids?: number[];
  department_Ids?: number[];
  crm_Name: string;
  stage_Url: string;
  live_Url: string;
  endpoint: string;
  roles_Endpoint?: string;
  http_Method: string;
  auth_Type: string;
  auth_Token?: string;
  username?: string;
  password?: string;
  headers_Json?: string;
  payload_Template?: string;
  description?: string;
  is_Active: boolean;
}

export const emptyCrmMapping: ICrmMapping = {
  company_Id: null,
  branch_Id: null,
  department_Id: null,
  company_Ids: [],
  branch_Ids: [],
  department_Ids: [],
  crm_Name: "",
  stage_Url: "",
  live_Url: "",
  endpoint: "/api/v1/users/provision",
  roles_Endpoint: "/api/roles",
  http_Method: "POST",
  auth_Type: "Bearer",
  auth_Token: "",
  username: "",
  password: "",
  headers_Json: "",
  payload_Template: "",
  description: "",
  is_Active: true,
};

export interface ICrmProviderPreset {
  name: string;
  description: string;
  badgeColor: string;
  iconText: string;
  defaultStageUrl: string;
  defaultLiveUrl: string;
  defaultEndpoint: string;
  defaultAuthType: string;
}

export const crmProviderPresets: ICrmProviderPreset[] = [
  {
    name: "Teklense",
    description: "In-house Staffing & Recruitment CRM",
    badgeColor: "teal",
    iconText: "TK",
    defaultStageUrl: "https://stage-api.teklense.com",
    defaultLiveUrl: "https://api.teklense.com",
    defaultEndpoint: "/api/v1/users/provision",
    defaultAuthType: "Bearer",
  },
  {
    name: "Bivocal",
    description: "Digital & Client Engagement CRM",
    badgeColor: "purple",
    iconText: "BV",
    defaultStageUrl: "https://staging.bivocal.com",
    defaultLiveUrl: "https://bivocal.com",
    defaultEndpoint: "/api/auth/register-employee",
    defaultAuthType: "Bearer",
  },
  {
    name: "Kommuno",
    description: "Cloud Telephony & Call CRM",
    badgeColor: "blue",
    iconText: "KM",
    defaultStageUrl: "https://softphonev1.kommuno.com",
    defaultLiveUrl: "https://softphonev1.kommuno.com",
    defaultEndpoint: "/v1/kcrm/createAgent",
    defaultAuthType: "ApiKey",
  },
  {
    name: "Salesforce / Zoho",
    description: "Third-party Enterprise CRM Webhook",
    badgeColor: "cyan",
    iconText: "SF",
    defaultStageUrl: "https://sandbox.api.crm.com",
    defaultLiveUrl: "https://api.crm.com",
    defaultEndpoint: "/services/oauth2/users",
    defaultAuthType: "Bearer",
  },
  {
    name: "Custom Gateway",
    description: "Dedicated REST Endpoint or Webhook",
    badgeColor: "gray",
    iconText: "CG",
    defaultStageUrl: "",
    defaultLiveUrl: "",
    defaultEndpoint: "/api/user/onboard",
    defaultAuthType: "Bearer",
  },
];

const urlRegex = /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;

export const crmMappingSchema = yup.object({
  company_Id: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" || o === undefined || o === 0 ? null : v)),
  branch_Id: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" || o === undefined || o === 0 ? null : v)),
  department_Id: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" || o === undefined || o === 0 ? null : v)),
  crm_Name: yup
    .string()
    .required("CRM Name is required")
    .trim()
    .min(2, "CRM Name must be at least 2 characters")
    .max(100, "CRM Name must be under 100 characters"),
  stage_Url: yup
    .string()
    .required("Staging URL is required")
    .trim(),
  live_Url: yup
    .string()
    .required("Live / Production URL is required")
    .trim(),
  roles_Endpoint: yup.string().nullable().notRequired().default("/api/roles"),
  endpoint: yup
    .string()
    .required("Provisioning Endpoint is required")
    .trim(),
  http_Method: yup.string().default("POST"),
  auth_Type: yup.string().required("Authentication type is required").default("Bearer"),
  auth_Token: yup.string().nullable().notRequired(),
  username: yup.string().nullable().notRequired(),
  password: yup.string().nullable().notRequired(),
  headers_Json: yup
    .string()
    .nullable()
    .test("is-valid-json", "Headers must be a valid JSON object (e.g. {\"X-Custom-Header\": \"value\"})", (value) => {
      if (!value || value.trim() === "") return true;
      try {
        const parsed = JSON.parse(value);
        return typeof parsed === "object" && !Array.isArray(parsed);
      } catch {
        return false;
      }
    }),
  payload_Template: yup
    .string()
    .nullable()
    .test("is-valid-json-payload", "Payload Template must be a valid JSON structure", (value) => {
      if (!value || value.trim() === "") return true;
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    }),
  description: yup.string().nullable().max(500, "Description cannot exceed 500 characters"),
  is_Active: yup.boolean().default(true),
});
